import React from 'react'

import {Colors} from '../../../components/UI/index.js'
import {parsePrice} from '../../../../utils/price.js'

// Small presentational building blocks for the return portal. All components take the customer's brand
// color as `color` so the portal matches the webshop.

export const BORDER = '#E5E7EB'

// Text tones for a white background (TEXT.muted/textMedium are meant for dark or neutral backgrounds)
export const TEXT = {
    strong: '#1F2328',
    body: '#3F4550',
    muted: '#6B7280'
}

export const CARRIER_LOGOS = {
    DHL: '/images/dhlLogo.png',
    PostNL: '/images/postnlLogo.png',
    GLS: '/images/glsLogo.png',
    DPD: '/images/dpdLogo.png',
    Bpost: '/images/bpostLogo.png'
}

export function carrierDisplayName(carrier = '') {
    return carrier.split('-')[0].trim()
}

export function formatCosts(costs) {
    return !costs || parsePrice(costs) === 0 ? 'Gratis' : `€ ${costs}`
}

// Hover/focus states and animations can't be expressed with inline styles
export function PortalStyles({color}) {
    return (
        <style>{`
            .rp-page { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .rp-fade-in { animation: rp-fade-in 240ms ease-out; }
            @keyframes rp-fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
            .rp-tile { transition: border-color 120ms, box-shadow 120ms, background 120ms; cursor: pointer; }
            .rp-tile:hover { border-color: ${color}; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06); }
            .rp-link { color: ${color}; cursor: pointer; font-weight: 500; background: none; border: none; padding: 0; font-size: 13px; }
            .rp-link:hover { text-decoration: underline; }
            .rp-spin { animation: rp-spin 900ms linear infinite; display: inline-block; }
            @keyframes rp-spin { to { transform: rotate(360deg); } }
        `}
        </style>
    )
}

export function StepTitle({title, subtitle}) {
    return (
        <div style={{marginBottom: 24}}>
            <h2 style={{margin: 0, fontSize: 22, fontWeight: 600, color: TEXT.strong}}>{title}</h2>
            {subtitle && <p style={{margin: '6px 0 0', fontSize: 14, color: TEXT.muted, lineHeight: 1.5}}>{subtitle}</p>}
        </div>
    )
}

export function Stepper({steps, current, color}) {
    return (
        <div style={{display: 'flex', gap: 6, marginBottom: 28}}>
            {steps.map((step, index) => (
                <div key={step.key} style={{flex: 1}}>
                    <div
                        style={{
                            height: 4,
                            borderRadius: 2,
                            background: index <= current ? color : Colors.grey20,
                            transition: 'background 200ms'
                        }}
                    />
                    <div
                        style={{
                            marginTop: 6,
                            fontSize: 11,
                            fontWeight: index === current ? 600 : 400,
                            color: index <= current ? TEXT.body : TEXT.muted,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {step.label}
                    </div>
                </div>
            ))}
        </div>
    )
}

// Selectable card with a radio indicator
export function OptionTile({selected, color, onClick, children}) {
    return (
        <div
            className='rp-tile'
            role='radio'
            aria-checked={selected}
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => (event.key === 'Enter' || event.key === ' ') && onClick()}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: 16,
                marginBottom: 10,
                borderRadius: 12,
                border: `1.5px solid ${selected ? color : BORDER}`,
                background: selected ? `${color}0D` : Colors.white
            }}
        >
            <div
                style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `2px solid ${selected ? color : Colors.grey40}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {selected && <div style={{width: 8, height: 8, borderRadius: '50%', background: color}} />}
            </div>

            <div style={{flex: 1, minWidth: 0}}>{children}</div>
        </div>
    )
}

export function Panel({children, style = {}}) {
    return (
        <div style={{padding: 16, marginBottom: 10, borderRadius: 12, border: `1px solid ${BORDER}`, background: Colors.white, ...style}}>
            {children}
        </div>
    )
}

// Read-only block in an overview, optionally with an edit link
export function InfoSection({title, onEdit, children}) {
    return (
        <div style={{padding: '16px 0', borderBottom: `1px solid ${BORDER}`}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8}}>
                <span style={{fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: TEXT.muted}}>{title}</span>
                {onEdit && <button className='rp-link' onClick={onEdit}>Wijzig</button>}
            </div>

            <div style={{fontSize: 14, color: TEXT.body, lineHeight: 1.6}}>{children}</div>
        </div>
    )
}

const TONES = {
    success: {background: Colors.successPale, color: Colors.successDark, icon: 'mdi-check-circle'},
    info: {background: Colors.infoPale, color: Colors.infoDark, icon: 'mdi-information'},
    warning: {background: Colors.warningPale, color: Colors.warningDark, icon: 'mdi-alert'},
    error: {background: Colors.errorPale, color: Colors.errorDark, icon: 'mdi-alert-circle'},
    loading: {background: Colors.infoPale, color: Colors.infoDark, icon: 'mdi-loading rp-spin'}
}

export function StatusBanner({tone = 'info', title, children, style = {}}) {
    const {background, color, icon} = TONES[tone]

    return (
        <div className='rp-fade-in' style={{display: 'flex', gap: 12, padding: 16, borderRadius: 12, background, marginBottom: 16, ...style}}>
            <i className={`mdi ${icon}`} style={{fontSize: 22, lineHeight: '22px', color}} />

            <div style={{flex: 1}}>
                {title && <div style={{fontWeight: 600, color, marginBottom: children ? 4 : 0}}>{title}</div>}
                {children && <div style={{fontSize: 14, color: TEXT.body, lineHeight: 1.5}}>{children}</div>}
            </div>
        </div>
    )
}
